/**
 * configureGltfLoaders — wire compression decoders onto a GLTFLoader.
 *
 * The viewer ships Meshopt-compressed geometry (EXT_meshopt_compression)
 * and KTX2/Basis-compressed textures (KHR_texture_basisu). Neither decodes
 * unless the GLTFLoader has the matching decoder attached, so this module
 * centralises that wiring for both load paths:
 *
 *   - the render hot-path via drei's `useGLTF` (see `useGltfClone.ts`), and
 *   - the public preload API (`AssetLoader`).
 *
 * Design: pure, framework-free functions so the wiring is unit-testable with
 * fake loader / renderer objects (node has no WebGL2, so KTX2 transcoding
 * itself is exercised in the browser — see docs/performance-analysis.md).
 *
 * Meshopt geometry decodes headless (no GPU needed) and is therefore always
 * attached. KTX2 transcoding needs the live `WebGLRenderer` to detect the
 * GPU's supported compressed-texture formats, so it is only attached when a
 * renderer is provided. A loader configured this way still loads plain,
 * uncompressed GLBs unchanged — attaching decoders is backward-compatible.
 */

import { KTX2Loader, MeshoptDecoder } from 'three-stdlib';
import type { GLTFLoader } from 'three-stdlib';
import type { WebGLRenderer } from 'three';
import { REVISION } from 'three';

/**
 * Build the default KTX2 transcoder (basis) path, pinned to the three.js
 * revision this build links against. The basis transcoder is shipped inside
 * the `three` npm package under `examples/jsm/libs/basis/`, so jsDelivr
 * serves a version-matched copy without bloating our own bundle.
 */
export function buildTranscoderUrl(revision: string | number = REVISION): string {
  return `https://cdn.jsdelivr.net/npm/three@0.${revision}.0/examples/jsm/libs/basis/`;
}

// Module-level override for the transcoder path (set via the public option /
// <XtsViewer3D> prop). `null` means "use the version-pinned default".
let transcoderUrlOverride: string | null = null;

/**
 * Override the KTX2 transcoder path globally. Passing `null` restores the
 * version-pinned default. Resets the cached KTX2Loader so the new path is
 * picked up on the next load.
 */
export function setKtx2TranscoderUrl(url: string | null): void {
  transcoderUrlOverride = url;
  ktx2Loader = null;
}

function resolveTranscoderUrl(explicit?: string): string {
  return explicit ?? transcoderUrlOverride ?? buildTranscoderUrl();
}

// Single shared KTX2Loader. Building one per GLB would spawn redundant
// transcoder worker pools; a singleton keeps memory + startup cost flat.
let ktx2Loader: KTX2Loader | null = null;

/**
 * Return the shared KTX2Loader, lazily constructing it on first use.
 * Idempotent: repeated calls return the same instance (until the transcoder
 * URL is changed via `setKtx2TranscoderUrl`).
 */
export function getKtx2Loader(transcoderUrl?: string): KTX2Loader {
  if (!ktx2Loader) {
    ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath(resolveTranscoderUrl(transcoderUrl));
  }
  return ktx2Loader;
}

export interface ConfigureLoaderOptions {
  /**
   * The live WebGLRenderer. Required for KTX2 textures — the loader must
   * detect which GPU-compressed formats the device supports. When omitted
   * (SSR / node tests), only Meshopt geometry decoding is wired up.
   */
  gl?: WebGLRenderer | null;
  /** Override the KTX2 transcoder (basis) path for this call. */
  transcoderUrl?: string;
}

/**
 * Attach the Meshopt decoder (always) and, when a renderer is available, the
 * KTX2 texture loader to `loader`. Returns the same loader for chaining.
 */
export function configureLoader(
  loader: GLTFLoader,
  options: ConfigureLoaderOptions = {},
): GLTFLoader {
  // Mirror drei's handling: MeshoptDecoder may be exported as a factory.
  const decoder =
    typeof MeshoptDecoder === 'function' ? MeshoptDecoder() : MeshoptDecoder;
  loader.setMeshoptDecoder(decoder);

  if (options.gl) {
    const ktx2 = getKtx2Loader(options.transcoderUrl);
    ktx2.detectSupport(options.gl);
    loader.setKTX2Loader(ktx2);
  }

  return loader;
}

/** Test-only: drop the cached KTX2Loader + transcoder override. */
export function resetGltfLoadersForTests(): void {
  ktx2Loader = null;
  transcoderUrlOverride = null;
}
